import { homeSections } from './homeSections'
import type { PageContent } from '../core/types/pages'

/**
 * De opbouw van elke pagina zoals de site hem toont (docs/VORMGEVING.md › Pagina's): één bron
 * voor de volgorde in de pagina-editor (Paginateksten). Per blok de titel, de rol in het verhaal,
 * de toon van het vlak en het ankertje op de pagina (voor "Bekijk dit blok").
 *
 * Meerdere tekstblokken kunnen bij één vlak op de site horen (bijv. "stappen" en "cadeaus" op
 * Protocollen): die delen dan het ankertje. `labels` = hoe een veld op díe plek heet, als het
 * algemene label (adminLabels.ts) niet genoeg zegt.
 */
export type Tone = 'sand' | 'shell' | 'blush' | 'sage' | 'clay'

export type PageBlock = {
  key: string
  title: string
  role: string
  tone?: Tone
  /** Ankertje op de pagina; leeg = bovenaan de pagina. */
  anchor: string
  labels?: Record<string, { label: string; hint?: string }>
}

export type EditablePage = Exclude<keyof PageContent, 'seo'>

const homeTones: Record<string, Tone> = {
  hero: 'sand',
  manifest: 'blush',
  story: 'sage',
  concerns: 'shell',
  protocols: 'sand',
  reviews: 'shell',
  bestsellers: 'sand',
  journal: 'clay',
  visit: 'blush',
}

const homeLabels: Record<string, PageBlock['labels']> = {
  hero: { cta: { label: 'Tweede knop (huidtest)', hint: 'De hoofdknop “Kies op je huidvraag” ligt vast. Leeg = alleen de hoofdknop.' } },
  story: { cta: { label: 'Knop (afspraak plannen)' } },
  concerns: { addCta: { label: 'Koopknop van het protocol', hint: 'Het bedrag staat er vanzelf naast.' } },
  visit: { cta: { label: 'Knop (afspraak plannen)' }, secondaryCta: { label: 'Link naar de route' } },
}

export const pageSections: Partial<Record<EditablePage, readonly PageBlock[]>> = {
  home: homeSections.map((s) => ({ ...s, tone: homeTones[s.key], labels: homeLabels[s.key] })),

  shop: [
    {
      key: 'hero',
      title: 'Kop',
      role: 'Wat past bij mijn huid? Hoofdknop “Kies op je huidvraag” (vast), de huidtest als tweede',
      tone: 'sand',
      anchor: '',
      labels: { secondaryCta: { label: 'Tweede knop (huidtest)', hint: 'Met het portret erbij. Leeg = niet tonen.' } },
    },
    { key: 'proof', title: 'Vertrouwensregel', role: 'De salon als bron van de keuze, onder de kop', tone: 'sand', anchor: '' },
    { key: 'routines', title: 'Protocollen', role: 'Voor wie twijfelt tussen losse producten', tone: 'sand', anchor: 'shop-routines' },
    { key: 'ingredients', title: 'Wat er écht in zit', role: 'Wáárom iets werkt, per stof', tone: 'shell', anchor: 'ingredients-title' },
    { key: 'brands', title: 'Merken', role: 'Waarom juist deze merken (de merken zelf: Aanbod › Merken)', tone: 'shell', anchor: 'shop-brands' },
  ],

  routines: [
    {
      key: 'hero',
      title: 'Opening',
      role: 'Wat gebruik ik samen? Hoofdknop “Begin met je protocol” (vast), de huidtest als tweede',
      tone: 'sand',
      anchor: '',
      labels: { cta: { label: 'Tweede knop (huidtest)' } },
    },
    { key: 'howHeading', title: 'Zo werkt het · kop', role: 'Kop boven de drie stappen', tone: 'shell', anchor: 'routines-how' },
    { key: 'steps', title: 'Zo werkt het · stappen', role: 'Van vragen tot meekijken, in volgorde', tone: 'shell', anchor: 'routines-how' },
    { key: 'gifts', title: 'Zo werkt het · cadeaus', role: 'Wat er kosteloos bij komt (de treden: Instellingen)', tone: 'shell', anchor: 'routines-how' },
    { key: 'anchorEyebrow', title: 'Het complete protocol', role: 'Het pronkstuk; het protocol zelf beheer je bij Aanbod › Protocollen', tone: 'blush', anchor: 'anchor-routine' },
    { key: 'others', title: 'Per klacht', role: 'Of begin met wat nu het meest stoort', tone: 'sand', anchor: 'other-routines' },
    { key: 'compareHeading', title: 'Vergelijken', role: 'Kop boven de tabel', tone: 'sand', anchor: 'other-routines' },
    {
      key: 'smallStart',
      title: 'Klein beginnen',
      role: 'Onder de vergelijking: los beginnen of eerst de huidtest',
      tone: 'sand',
      anchor: 'other-routines',
      labels: { body: { label: 'Zin' }, shopCta: { label: 'Link naar de webshop' }, quizCta: { label: 'Link naar de huidtest' } },
    },
    {
      key: 'salonClient',
      title: 'Al klant in de salon?',
      role: 'Afsluiter: de salon als bron',
      tone: 'blush',
      anchor: 'salon-client',
      labels: { cta: { label: 'Knop (opent WhatsApp)' } },
    },
  ],

  treatments: [
    {
      key: 'hero',
      title: 'Opening',
      role: 'Kan ik langskomen? Hoofdknop: plan een afspraak',
      tone: 'sand',
      anchor: '',
      labels: {
        cta: { label: 'Hoofdknop (afspraak plannen)' },
        secondaryCta: { label: 'Tweede knop (naar de lijst)' },
        treatedBy: { label: 'Regel bij de foto', hint: 'Gevolgd door de naam. Beloof niet dat je alles zelf doet: er werkt ook een collega.' },
      },
    },
    { key: 'listHeading', title: 'Alle behandelingen · kop', role: 'Wie hier komt, wil kiezen', tone: 'shell', anchor: 'behandelingen' },
    { key: 'includes', title: 'Alle behandelingen · inbegrepen', role: 'Wat bij elke behandeling zit', tone: 'shell', anchor: 'behandelingen' },
    { key: 'team', title: 'Ook in de salon', role: 'Collega’s, alleen als ze op de site staan (Instellingen › Team)', tone: 'sand', anchor: 'salon-team' },
    {
      key: 'bindweefsel',
      title: 'Bindweefselmassage',
      role: 'Waarom het werkt: het ene verhaal op klei, het enige bewegende beeld',
      tone: 'clay',
      anchor: 'bindweefsel',
      labels: { cta: { label: 'Knop (afspraak bindweefselmassage)' }, secondaryCta: { label: 'Link naar de webshop' } },
    },
    {
      key: 'bridge',
      title: 'Van salon naar thuis',
      role: 'De brug naar de webshop',
      tone: 'sand',
      anchor: 'salon-bridge',
      labels: { cta: { label: 'Knop naar de protocollen' }, secondaryCta: { label: 'Tweede knop (webshop)' } },
    },
    { key: 'faqTitle', title: 'Praktisch', role: 'Kop boven de vragen over je eerste afspraak', tone: 'shell', anchor: 'salon-vragen' },
    {
      key: 'closing',
      title: 'Twijfel',
      role: 'Afsluiter op blush',
      tone: 'blush',
      anchor: 'salon-closing',
      labels: { cta: { label: 'Knop (afspraak, met het consult gekozen)' }, secondaryCta: { label: 'WhatsApp-link' } },
    },
  ],

  journal: [
    { key: 'hero', title: 'Kop', role: 'Wat weet een huidspecialist dat ik nog niet weet?', tone: 'sand', anchor: '' },
    { key: 'articlesHeading', title: 'Artikelen · kop', role: 'Boven het nieuwste artikel en het raster', tone: 'sand', anchor: 'journal-articles' },
    { key: 'emptyCategory', title: 'Artikelen · lege categorie', role: 'Als een categorie (nog) leeg is', tone: 'sand', anchor: 'journal-articles' },
    { key: 'podcast', title: 'De podcast', role: 'Op klei', tone: 'clay', anchor: 'podcast-title' },
    { key: 'episodes', title: 'Afleveringen', role: 'In de podcast, nieuwste eerst', tone: 'clay', anchor: 'podcast-title' },
  ],

  about: [
    {
      key: 'hero',
      title: 'Opening en verhaal',
      role: 'Wie kiest dit? Hoofdknop “Bekijk de collectie” (vast), een afspraak als tweede',
      tone: 'sand',
      anchor: '',
      labels: { cta: { label: 'Tweede knop (afspraak plannen)' }, paragraphs: { label: 'Alinea’s', hint: 'De eerste staat in de opening, de rest eronder.' } },
    },
    { key: 'philosophy', title: 'Waar ik voor sta', role: 'Haar filosofie, op klei, het enige bewegende beeld', tone: 'clay', anchor: 'filosofie' },
    { key: 'shelf', title: 'Haar plank', role: 'Wat ze zelf gebruikt, zes producten', tone: 'shell', anchor: 'about-shelf' },
    {
      key: 'salon',
      title: 'Kom kennismaken',
      role: 'Afsluiter op blush (het gedeelde blok “kom langs”)',
      tone: 'blush',
      anchor: 'about-salon',
      labels: { cta: { label: 'Knop (afspraak plannen)' } },
    },
  ],

  contact: [
    {
      key: 'hero',
      title: 'Kop',
      role: 'Hoe krijg ik snel antwoord? Hoofdknop: WhatsApp',
      tone: 'sand',
      anchor: '',
      labels: { cta: { label: 'Hoofdknop (opent WhatsApp)' }, secondaryCta: { label: 'Tweede knop (mail)' } },
    },
    { key: 'channelsTitle', title: 'Soorten vragen · kop', role: 'Naast de kop', tone: 'sand', anchor: '' },
    { key: 'channels', title: 'Soorten vragen', role: 'Elke vraag met de snelste weg', tone: 'sand', anchor: '' },
    { key: 'visit', title: 'Langskomen', role: 'Kaart, route en tijden: de lange versie van “kom langs”', tone: 'blush', anchor: 'langskomen' },
  ],
}
